import type { ReactElement } from "react";

import { ExploreTestID } from "@/constants/TestID";
import GroupClassSessionCard from "@/ui/components/groupClasses/series/session/GroupClassSessionCard";
import type { AvatarCardProps } from "@/ui/core";
import { Text } from "@/ui/core";
import { FlatList, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";

// data for a session row; the list owns selection/press/testID, so those aren't part of
// the item. Declared independently of the card props to keep the data shape decoupled.
interface GroupClassSession {
  hosts?: AvatarCardProps[];
  id: string;
  isOngoing?: boolean;
  sessionCount: number;
  timeRange: string;
  title: string;
}

interface GroupClassSessionListProps {
  description?: string;
  // rendered at the end of the list, scrolling with the content (e.g. the edit
  // sheet's "Unregister" button, so it isn't a sticky floating button)
  footer?: ReactElement;
  // when the list is rendered inside a gorhom bottom sheet (e.g. the edit-registration
  // sheet), this wires the FlashList into the sheet's gesture system so it scrolls on Android
  isInsideBottomSheet?: boolean;
  onSelect: (id: string) => void;
  // selection mode: highlights the matching card and hides its chevron
  selectedId?: string;
  sessions: GroupClassSession[];
  shouldHideChevron?: boolean;
  title?: string;
}

// both optional: the edit-registration sheet renders the list with only a description
// (its title lives in the sheet header), while the details/recommended views pass both
interface ListHeaderProps {
  description?: string;
  title?: string;
}

const ListHeader = ({ description, title }: ListHeaderProps) => {
  const hasTitle = title && title.length > 0;
  const hasDescription = description && description.length > 0;
  if (!hasTitle && !hasDescription) return null;

  return (
    <VStack gap={4} paddingBottom={16}>
      {hasTitle && (
        <Text testID={ExploreTestID.GroupClassSessionListTitle} variant="h5">
          {title}
        </Text>
      )}
      {hasDescription && (
        <Text
          color={ColorToken.NeutralSecondary}
          testID={ExploreTestID.GroupClassSessionListDescription}
          variant="body"
        >
          {description}
        </Text>
      )}
    </VStack>
  );
};

const GroupClassSessionList = ({
  description,
  footer,
  isInsideBottomSheet,
  onSelect,
  selectedId,
  sessions,
  shouldHideChevron,
  title,
}: GroupClassSessionListProps) => {
  return (
    <FlatList
      data={sessions}
      isInsideBottomSheet={isInsideBottomSheet}
      keyExtractor={(item) => item.id}
      ListFooterComponent={footer}
      ListHeaderComponent={
        <ListHeader description={description} title={title} />
      }
      renderItem={({ item }) => (
        <GroupClassSessionCard
          hosts={item.hosts}
          isOngoing={item.isOngoing}
          isSelected={item.id === selectedId}
          onPress={() => onSelect(item.id)}
          sessionCount={item.sessionCount}
          shouldHideChevron={shouldHideChevron}
          testID={item.id}
          timeRange={item.timeRange}
          title={item.title}
        />
      )}
      separatorPadding={12}
      shouldRemoveStyleHorizontalPadding
      testID={ExploreTestID.GroupClassSessionList}
    />
  );
};

export type { GroupClassSession, GroupClassSessionListProps };
export default GroupClassSessionList;
